import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

import ProfileSettings from './components/ProfileSettings';
import SecuritySettings from './components/SecuritySettings';
import VerificationSettings from './components/VerificationSettings';
import PreferencesSettings from './components/PreferencesSettings';
import ApiManagement from './components/ApiManagement';

const AccountSettings = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('profile');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const settingSections = [
    {
      id: 'profile',
      label: 'Profile',
      icon: 'User',
      description: 'Personal information and contact details'
    },
    {
      id: 'security',
      label: 'Security',
      icon: 'Shield',
      description: 'Password, 2FA, and device management'
    },
    {
      id: 'verification',
      label: 'Verification',
      icon: 'CheckCircle',
      description: 'KYC/AML compliance and identity verification'
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: 'Settings',
      description: 'Trading settings and notifications'
    },
    {
      id: 'api',
      label: 'API Management',
      icon: 'Code',
      description: 'API keys and developer settings'
    }
  ];

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/trading-dashboard' },
    { label: 'Account Settings', path: '/account-settings' }
  ];

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSettings />;
      case 'security':
        return <SecuritySettings />;
      case 'verification':
        return <VerificationSettings />;
      case 'preferences':
        return <PreferencesSettings />;
      case 'api':
        return <ApiManagement />;
      default:
        return <ProfileSettings />;
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setIsMobileSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center space-x-2 text-sm mb-8">
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={item.path}>
              {index > 0 && (
                <Icon name="ChevronRight" size={16} className="text-text-secondary" />
              )}
              <button
                onClick={() => navigate(item.path)}
                className={`font-body-medium transition-colors duration-trading-fast ${
                  index === breadcrumbItems.length - 1
                    ? 'text-text-primary cursor-default' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </nav>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading-bold text-text-primary mb-2">Account Settings</h1>
          <p className="text-text-secondary font-body-normal">
            Manage your profile, security settings, and trading preferences
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Sidebar Toggle */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
              className="w-full flex items-center justify-between p-4 bg-surface border border-border rounded-trading"
            >
              <div className="flex items-center space-x-3">
                <Icon 
                  name={settingSections.find(s => s.id === activeSection)?.icon || 'Settings'} 
                  size={20} 
                  className="text-primary" 
                />
                <span className="font-body-medium text-text-primary">
                  {settingSections.find(s => s.id === activeSection)?.label}
                </span>
              </div>
              <Icon 
                name={isMobileSidebarOpen ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                className="text-text-secondary" 
              />
            </button>

            {/* Mobile Sidebar Menu */}
            {isMobileSidebarOpen && (
              <div className="mt-2 bg-surface border border-border rounded-trading overflow-hidden">
                {settingSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionChange(section.id)}
                    className={`w-full flex items-center space-x-3 p-4 text-left transition-colors duration-trading-fast border-b border-border last:border-b-0 ${
                      activeSection === section.id
                        ? 'bg-primary-50 text-primary border-l-4 border-l-primary' :'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
                    }`}
                  >
                    <Icon name={section.icon} size={20} />
                    <div>
                      <div className="font-body-medium">{section.label}</div>
                      <div className="text-xs text-text-secondary">{section.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="bg-surface border border-border rounded-trading overflow-hidden">
              <div className="p-6 border-b border-border">
                <h2 className="text-lg font-heading-semibold text-text-primary mb-2">Settings</h2>
                <p className="text-sm text-text-secondary">Configure your account preferences</p>
              </div>
              
              <nav className="p-2">
                {settingSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionChange(section.id)}
                    className={`w-full flex items-center space-x-3 p-4 rounded-trading text-left transition-all duration-trading-fast mb-1 ${
                      activeSection === section.id
                        ? 'bg-primary-50 text-primary border border-primary-100' :'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
                    }`}
                  >
                    <Icon name={section.icon} size={20} />
                    <div className="flex-1">
                      <div className="font-body-medium">{section.label}</div>
                      <div className="text-xs text-text-secondary mt-1">{section.description}</div>
                    </div>
                    {activeSection === section.id && (
                      <Icon name="ChevronRight" size={16} className="text-primary" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Account Status Card */}
            <div className="mt-6 bg-surface border border-border rounded-trading p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-body-medium text-text-primary">John Trader</h3>
                  <p className="text-sm text-text-secondary">Premium Account</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Verification Status</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm font-body-medium text-success">Verified</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">2FA Status</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Shield" size={16} className="text-success" />
                    <span className="text-sm font-body-medium text-success">Enabled</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Account Level</span>
                  <span className="text-sm font-body-medium text-text-primary">Level 3</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="bg-surface border border-border rounded-trading">
              {renderActiveSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;